import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  FileSignature, Send, RotateCcw, Eye, Download, Link2, Clock, Check,
  X, Search, RefreshCw, Settings, ShieldCheck, AlertTriangle,
} from "lucide-react";

const STATUS_STYLES = {
  not_sent:  { label: "Not Sent",  cls: "bg-white/8 text-white/60 ring-white/15",     icon: Clock },
  draft:     { label: "Draft",     cls: "bg-amber-400/15 text-amber-300 ring-amber-400/40", icon: Clock },
  sent:      { label: "Sent",      cls: "bg-cyan/15 text-cyan ring-cyan/40",           icon: Send },
  viewed:    { label: "Viewed",    cls: "bg-indigo-400/15 text-indigo-300 ring-indigo-400/40", icon: Eye },
  signed:    { label: "Signed",    cls: "bg-lime-400/15 text-lime-300 ring-lime-400/40", icon: Check },
  completed: { label: "Completed", cls: "bg-lime-400/25 text-lime-200 ring-lime-400/60", icon: Check },
  declined:  { label: "Declined",  cls: "bg-red-500/15 text-red-300 ring-red-500/40",  icon: X },
  expired:   { label: "Expired",   cls: "bg-orange-500/15 text-orange-300 ring-orange-500/40", icon: Clock },
  error:     { label: "Error",     cls: "bg-red-500/25 text-red-200 ring-red-500/60",  icon: AlertTriangle },
};

function StatusPill({ status }) {
  const meta = STATUS_STYLES[status] || STATUS_STYLES.not_sent;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] label-caps ring-1 ${meta.cls}`}>
      <Icon size={10} /> {meta.label}
    </span>
  );
}

function SettingsPanel() {
  const [cfg, setCfg] = useState(null);
  const [testing, setTesting] = useState(false);
  const [templateInput, setTemplateInput] = useState("");

  const load = () => api.get("/admin/pandadoc/settings")
    .then((r) => {
      setCfg(r.data);
      setTemplateInput(r.data.template_uuid || "");
    })
    .catch(() => toast.error("Couldn't load PandaDoc settings"));

  useEffect(load, []);

  const saveTemplate = async () => {
    try {
      await api.patch("/admin/pandadoc/settings", { template_uuid: templateInput.trim() });
      toast.success("Template saved");
      load();
    } catch (e) { toast.error("Save failed"); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      await api.post("/admin/pandadoc/test-connection");
      toast.success("PandaDoc connection OK");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection failed");
    } finally { setTesting(false); }
  };

  if (!cfg) return <div className="text-white/50 text-sm">Loading settings…</div>;

  return (
    <div data-testid="pandadoc-settings" className="rounded-2xl border border-white/10 bg-black/30 p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Settings size={16} className="text-cyan" />
        <div className="label-caps text-[10px] tracking-widest text-white/60">PandaDoc integration</div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <StatusRow label="API key" ok={cfg.api_key_configured}
          hint={cfg.api_key_configured ? "Configured in server env" : "Set PANDADOC_API_KEY in backend/.env"} />
        <StatusRow label="Webhook shared key" ok={cfg.webhook_configured}
          hint={cfg.webhook_configured ? "Configured" : "Set PANDADOC_WEBHOOK_SHARED_KEY in backend/.env"} />
      </div>
      <label className="block">
        <div className="label-caps text-[9px] text-white/50 mb-1">Template UUID</div>
        <div className="flex gap-2">
          <input
            data-testid="pandadoc-template-input"
            value={templateInput}
            onChange={(e) => setTemplateInput(e.target.value)}
            placeholder="Paste your PandaDoc template UUID"
            className="flex-1 bg-ink-800 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-cyan"
          />
          <button type="button" onClick={saveTemplate}
            data-testid="pandadoc-save-template"
            className="px-4 py-2 rounded-full bg-cyan text-black text-xs font-semibold">Save</button>
        </div>
        <div className="text-[11px] text-white/40 mt-1">
          Tip: also set <code className="text-cyan">PANDADOC_TEMPLATE_UUID</code> in backend/.env so the value survives redeploys.
        </div>
      </label>
      <div className="text-[11px] text-white/50 border-t border-white/8 pt-3">
        <b className="text-white/70">Webhook URL to enter in PandaDoc:</b>{" "}
        <code className="text-cyan">{window.location.origin}/api/webhooks/pandadoc</code>
      </div>
      <button
        type="button"
        onClick={testConnection}
        disabled={testing || !cfg.api_key_configured}
        data-testid="pandadoc-test-connection"
        className="px-4 py-2 rounded-full border border-cyan/50 text-cyan hover:bg-cyan/10 text-xs label-caps inline-flex items-center gap-1.5 disabled:opacity-40"
      >
        <ShieldCheck size={12} /> {testing ? "Testing…" : "Test connection"}
      </button>
    </div>
  );
}

function StatusRow({ label, ok, hint }) {
  return (
    <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-white/70">{label}</div>
        <span className={`text-[10px] label-caps px-2 py-0.5 rounded-full ring-1 ${
          ok ? "bg-lime-400/15 text-lime-300 ring-lime-400/40"
             : "bg-red-500/15 text-red-300 ring-red-500/40"
        }`}>{ok ? "Connected" : "Not Set"}</span>
      </div>
      <div className="text-[10px] text-white/40 mt-1">{hint}</div>
    </div>
  );
}

export default function ContractsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/pandadoc/contracts")
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error("Couldn't load contracts"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => rows.filter((b) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [b.full_name, b.vehicle_name, b.reservation_id, b.id, b.customer_email, b.customer_phone]
      .filter(Boolean).some((f) => f.toLowerCase().includes(s));
  }), [rows, q]);

  const runAction = async (id, fn, label) => {
    setBusy(`${id}-${label}`);
    try {
      await fn();
      toast.success(label);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} failed`);
    } finally { setBusy(null); }
  };

  const send = (b) => runAction(b.id,
    () => api.post(`/admin/pandadoc/bookings/${b.id}/send-contract`),
    "Contract sent");
  const forceSend = (b) => runAction(b.id,
    () => api.post(`/admin/pandadoc/bookings/${b.id}/send-contract?force=true`),
    "Contract regenerated");
  const resend = (b) => runAction(b.id,
    () => api.post(`/admin/pandadoc/bookings/${b.id}/resend`),
    "Reminder sent");
  const refresh = (b) => runAction(b.id,
    () => api.get(`/admin/pandadoc/bookings/${b.id}/status`),
    "Status refreshed");
  const copyLink = async (b) => {
    try {
      const { data } = await api.post(`/admin/pandadoc/bookings/${b.id}/signing-link`);
      const url = window.location.origin + data.signing_url;
      await navigator.clipboard.writeText(url);
      toast.success("Signing link copied to clipboard");
    } catch (e) { toast.error("Couldn't create link"); }
  };
  const downloadPdf = (b) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/pandadoc/bookings/${b.id}/signed-pdf`, "_blank");
  };

  return (
    <div data-testid="bo-contracts-root" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps text-cyan text-[11px] mb-1">E-signature</div>
          <h2 className="font-serif text-2xl text-white flex items-center gap-2">
            <FileSignature className="text-cyan" size={20} /> Contracts &amp; PandaDoc
          </h2>
          <p className="text-white/50 text-xs mt-1 max-w-2xl">
            Send rental agreements to every renter with one click. Signed PDFs auto-download
            back to the reservation the moment a customer completes signing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-ink-800 border border-white/10 rounded-full px-3 py-2">
            <Search size={13} className="text-white/40" />
            <input
              data-testid="bo-contracts-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer, vehicle, reservation…"
              className="bg-transparent px-2 text-xs outline-none text-white/85 w-56"
            />
          </div>
          <button type="button" onClick={load}
            data-testid="bo-contracts-refresh"
            className="px-3 py-2 rounded-full border border-white/15 text-xs label-caps text-white/70 hover:text-white hover:border-white/30 inline-flex items-center gap-1.5">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      <SettingsPanel />

      {loading ? (
        <div className="text-white/50 text-sm text-center py-12">Loading contracts…</div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-black/30 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-white/50 text-[10px] label-caps">
              <tr>
                <th className="text-left px-4 py-3">Reservation</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Vehicle</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-white/40">No rental bookings yet.</td></tr>
              ) : filtered.map((b) => {
                const st = b.contract_status || "not_sent";
                const hasContract = !!b.pandadoc_document_id;
                const isSigned = ["signed", "completed"].includes(st);
                const rowBusy = busy?.startsWith(`${b.id}-`);
                return (
                  <tr key={b.id} data-testid={`contracts-row-${b.id}`}
                      className="border-t border-white/8 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="text-white font-mono text-[11px]">{b.reservation_id || b.id}</div>
                      <div className="text-white/40 text-[10px]">{(b.start_at || "").slice(0, 10)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white text-xs">{b.full_name || "—"}</div>
                      <div className="text-white/40 text-[10px]">{b.customer_email || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-white/80 text-xs">{b.vehicle_name || "—"}</td>
                    <td className="px-4 py-3"><StatusPill status={st} /></td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5 justify-end">
                        {!hasContract && (
                          <ActionBtn testId={`contract-send-${b.id}`} onClick={() => send(b)}
                            icon={Send} label="Send" disabled={rowBusy} />
                        )}
                        {hasContract && !isSigned && (
                          <>
                            <ActionBtn testId={`contract-resend-${b.id}`} onClick={() => resend(b)}
                              icon={RotateCcw} label="Resend" disabled={rowBusy} />
                            <ActionBtn testId={`contract-refresh-${b.id}`} onClick={() => refresh(b)}
                              icon={RefreshCw} label="Refresh" disabled={rowBusy} />
                            <ActionBtn testId={`contract-copylink-${b.id}`} onClick={() => copyLink(b)}
                              icon={Link2} label="Copy link" disabled={rowBusy} />
                          </>
                        )}
                        {hasContract && (
                          <ActionBtn testId={`contract-regenerate-${b.id}`} onClick={() => forceSend(b)}
                            icon={FileSignature} label="Regenerate" disabled={rowBusy} tone="warn" />
                        )}
                        {b.pandadoc_signed_pdf_id && (
                          <ActionBtn testId={`contract-pdf-${b.id}`} onClick={() => downloadPdf(b)}
                            icon={Download} label="Signed PDF" tone="lime" />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ActionBtn({ testId, onClick, icon: Icon, label, disabled, tone = "cyan" }) {
  const cls = {
    cyan: "border-cyan/40 text-cyan hover:bg-cyan/10",
    lime: "border-lime-400/40 text-lime-300 hover:bg-lime-400/10",
    warn: "border-amber-400/40 text-amber-300 hover:bg-amber-400/10",
  }[tone];
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      data-testid={testId}
      className={`px-2.5 py-1 rounded-full border text-[10px] label-caps inline-flex items-center gap-1 disabled:opacity-40 transition ${cls}`}>
      <Icon size={10} /> {label}
    </button>
  );
}
